// CandlePanel — chart de velas autónomo (sin priceTicker singleton)
// Usado en modo "Ambas" para mostrar Norte y Sur lado a lado.
// Hace su propio fetch directo al backend con polling cada 60s.

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { createChart } from 'lightweight-charts';
import { API_BASE_URL } from '../config/api';
import './CandlestickChart.css';

const PRECIO_OFICIAL = 7.50;
const POLL_MS = 60_000;

const TIMEFRAME_CONFIG = {
  '30m': { interval: 30 * 60,           maxCandles: 336 },
  '1h':  { interval: 60 * 60,           maxCandles: 336 },
  '1d':  { interval: 24 * 60 * 60,      maxCandles: 90  },
  '1w':  { interval: 7 * 24 * 60 * 60,  maxCandles: 52  },
  '1M':  { interval: 30 * 24 * 60 * 60, maxCandles: 24  },
};

const SPANISH_MONTHS_SHORT = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

const isoWeekFromTimestamp = (ts) => {
  const d = new Date(ts * 1000);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

const tickLabelForTimeframe = (timeframe, time) => {
  const d = new Date(time * 1000);
  if (timeframe === '1w') return `Wk${isoWeekFromTimestamp(time)}`;
  if (timeframe === '1M') return `${SPANISH_MONTHS_SHORT[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
  if (timeframe === '1d') return `${d.getDate()} ${SPANISH_MONTHS_SHORT[d.getMonth()]}`;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const longDateForTimeframe = (timeframe, time) => {
  const d = new Date(time * 1000);
  const day = d.getDate();
  const mon = SPANISH_MONTHS_SHORT[d.getMonth()];
  const yr  = d.getFullYear();
  if (timeframe === '1w') return `Wk${isoWeekFromTimestamp(time)} · semana del ${day} ${mon} ${yr}`;
  if (timeframe === '1M') return `${mon} ${yr}`;
  if (timeframe === '1d') return `${day} ${mon} ${yr}`;
  return `${day} ${mon} ${yr} · ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

const computeCandleStats = (data) => {
  if (!data?.length) return null;
  const first = data[0];
  const last  = data[data.length - 1];
  let high = -Infinity, low = Infinity, highCandle = first, lowCandle = first;
  for (const c of data) {
    if (c.high > high) { high = c.high; highCandle = c; }
    if (c.low  < low)  { low  = c.low;  lowCandle  = c; }
  }
  const change    = last.close - first.open;
  const changePct = first.open !== 0 ? (change / first.open) * 100 : 0;
  return { open: first.open, close: last.close, high, low, highCandle, lowCandle, change, changePct };
};

// Plain price points → OHLC + volume buckets
const convertToCandleData = (rawData, timeframe) => {
  const interval = TIMEFRAME_CONFIG[timeframe].interval;
  const candleMap = new Map();
  for (const point of rawData || []) {
    const ts = Math.floor(new Date(point.date).getTime() / 1000);
    const t  = Math.floor(ts / interval) * interval;
    const p  = parseFloat(point.price_per_box);
    if (Number.isNaN(p)) continue;
    if (candleMap.has(t)) {
      const c = candleMap.get(t);
      c.high   = Math.max(c.high, p);
      c.low    = Math.min(c.low, p);
      c.close  = p;
      c.volume = (c.volume || 1) + 1;
    } else {
      candleMap.set(t, { time: t, open: p, high: p, low: p, close: p, volume: 1 });
    }
  }
  return Array.from(candleMap.values()).sort((a, b) => a.time - b.time);
};

// Color por zona — Norte verde marca, Sur azul marca
const ZONE_COLOR = {
  norte: '#17c671',
  sur:   '#2962ff',
};

const CandlePanel = ({ zone, timeframe, label, height = 320 }) => {
  const containerRef    = useRef(null);
  const tooltipRef      = useRef(null);
  const chartRef        = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Crea el chart una vez por (zone, timeframe, height)
  useEffect(() => {
    if (!containerRef.current) return undefined;

    const chart = createChart(containerRef.current, {
      width:  containerRef.current.clientWidth,
      height,
      layout: {
        background: { color: '#131722' },
        textColor:  '#9ba5bc',
        fontSize:   11,
        fontFamily: "'Inter', system-ui, sans-serif",
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: 'rgba(42,46,57,0.4)', style: 1 },
        horzLines: { color: 'rgba(42,46,57,0.4)', style: 1 },
      },
      crosshair: {
        mode: 1,
        vertLine: { color: '#4a5568', width: 1, style: 3, labelBackgroundColor: '#1e2a3a' },
        horzLine: { color: '#4a5568', width: 1, style: 3, labelBackgroundColor: '#1e2a3a' },
      },
      rightPriceScale: { borderColor: 'rgba(42,46,57,0.6)' },
      timeScale: {
        borderColor:    'rgba(42,46,57,0.6)',
        timeVisible:    timeframe === '30m' || timeframe === '1h',
        secondsVisible: false,
        rightOffset:    6,
        barSpacing:     6,
        tickMarkFormatter: (time) => tickLabelForTimeframe(timeframe, time),
      },
    });

    const upColor = ZONE_COLOR[zone] || ZONE_COLOR.norte;
    const candleSeries = chart.addCandlestickSeries({
      upColor,
      downColor:       '#f53d6b',
      borderUpColor:   upColor,
      borderDownColor: '#f53d6b',
      wickUpColor:     upColor,
      wickDownColor:   '#f53d6b',
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    });
    candleSeries.priceScale().applyOptions({ scaleMargins: { top: 0.1, bottom: 0.26 } });

    candleSeries.createPriceLine({
      price: PRECIO_OFICIAL,
      color: 'rgba(155,165,188,0.5)',
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: true,
      title: 'Oficial',
    });

    const volumeSeries = chart.addHistogramSeries({
      priceFormat: { type: 'volume' },
      priceScaleId: '',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.78, bottom: 0 } });

    // Tooltip OHLC
    chart.subscribeCrosshairMove(param => {
      const tip = tooltipRef.current;
      const container = containerRef.current;
      if (!tip || !container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!param.time || !param.point || param.point.x < 0 || param.point.y < 0 || param.point.x > w || param.point.y > h) {
        tip.style.display = 'none';
        return;
      }
      const data = param.seriesData.get(candleSeries);
      if (!data || data.open == null) { tip.style.display = 'none'; return; }
      const dateLabel = longDateForTimeframe(timeframe, data.time);
      const change    = data.close - data.open;
      const changePct = data.open !== 0 ? (change / data.open) * 100 : 0;
      const changeColor = change >= 0 ? '#17c671' : '#f53d6b';
      const changeArrow = change >= 0 ? '▲' : '▼';
      tip.innerHTML = `
        <div class="cs-tip__date">${dateLabel}</div>
        <div class="cs-tip__row"><span class="cs-tip__label">Apertura</span><span class="cs-tip__value">$${data.open.toFixed(2)}</span></div>
        <div class="cs-tip__row"><span class="cs-tip__label">Máximo</span><span class="cs-tip__value">$${data.high.toFixed(2)}</span></div>
        <div class="cs-tip__row"><span class="cs-tip__label">Mínimo</span><span class="cs-tip__value">$${data.low.toFixed(2)}</span></div>
        <div class="cs-tip__row"><span class="cs-tip__label">Cierre</span><span class="cs-tip__value">$${data.close.toFixed(2)}</span></div>
        <div class="cs-tip__row" style="border-top:1px solid rgba(155,165,188,0.18); margin-top:4px; padding-top:6px">
          <span class="cs-tip__label">Variación</span>
          <span class="cs-tip__value" style="color:${changeColor}">${changeArrow} ${Math.abs(changePct).toFixed(2)}%</span>
        </div>
      `;
      tip.style.display = 'block';
      const tipW = tip.clientWidth, tipH = tip.clientHeight;
      let x = param.point.x + 16;
      let y = param.point.y - tipH - 16;
      if (x + tipW > w) x = param.point.x - tipW - 16;
      if (y < 0) y = param.point.y + 16;
      tip.style.left = `${x}px`;
      tip.style.top  = `${y}px`;
    });

    chartRef.current        = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    const onResize = () => {
      if (!containerRef.current) return;
      chart.applyOptions({ width: containerRef.current.clientWidth });
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      chartRef.current        = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      chart.remove();
    };
  }, [timeframe, zone, height]);

  // Fetch + polling cada 60s
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const config = TIMEFRAME_CONFIG[timeframe];
        const token = localStorage.getItem('access_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await axios.get(`${API_BASE_URL}/prices/historical-candlestick/`, {
          params: { timeframe, limit: config.maxCandles * 10, zone },
          headers,
          timeout: 25000,
        });
        if (cancelled) return;
        const candles = convertToCandleData(r.data || [], timeframe);
        if (!candleSeriesRef.current) return;
        candleSeriesRef.current.setData(candles);
        const s = computeCandleStats(candles);
        setStats(s);
        if (s) {
          candleSeriesRef.current.setMarkers([
            { time: s.highCandle.time, position: 'aboveBar', color: '#17c671', shape: 'arrowDown', text: `Máx $${s.high.toFixed(2)}` },
            { time: s.lowCandle.time,  position: 'belowBar', color: '#f53d6b', shape: 'arrowUp',   text: `Mín $${s.low.toFixed(2)}`  },
          ]);
        }
        if (volumeSeriesRef.current) {
          volumeSeriesRef.current.setData(candles.map(c => ({
            time:  c.time,
            value: c.volume || 1,
            color: c.close >= c.open ? 'rgba(23,198,113,0.45)' : 'rgba(245,61,107,0.45)',
          })));
        }
        chartRef.current?.timeScale().fitContent();
        setError(null);
      } catch (e) {
        if (!cancelled) setError('Error al cargar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    run();
    const id = setInterval(run, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [zone, timeframe]);

  const accent = ZONE_COLOR[zone] || ZONE_COLOR.norte;

  return (
    <div className="cs-pane">
      <div className="cs-pane__header">
        <div className="cs-pane__label" style={{ borderLeftColor: accent }}>
          {label}
        </div>
        {stats && (
          <div className="cs-pane__stats">
            <span className="cs-pane__price">${stats.close.toFixed(2)}</span>
            <span className={`cs-pane__change ${stats.change >= 0 ? 'cs-pane__change--up' : 'cs-pane__change--down'}`}>
              {stats.change >= 0 ? '▲' : '▼'} {Math.abs(stats.changePct).toFixed(2)}%
            </span>
            <span className="cs-pane__hl">Máx ${stats.high.toFixed(2)} · Mín ${stats.low.toFixed(2)}</span>
          </div>
        )}
      </div>
      <div className="cs-pane__chart-wrap">
        <div ref={containerRef} className="cs-pane__chart" style={{ height }} />
        <div ref={tooltipRef} className="cs-tip" />
        {loading && (
          <div className="cs-pane__loading">
            <div className="cs-spinner" />
          </div>
        )}
        {error && !loading && (
          <div className="cs-pane__error">{error}</div>
        )}
      </div>
    </div>
  );
};

export default CandlePanel;
